/**
 * ANKER-SLUGS TIL AFSNIT ([D-198] — det globale søgefelt).
 *
 * Søgningen skal kunne springe ned til det AFSNIT et træf hører til, og det
 * kræver et id på afsnittets overskrift. Reglen bor her, fordi den bruges to
 * steder der SKAL være enige:
 *
 *   1. `SectionTitle.vue` sætter id'et når siden tegnes.
 *   2. `scripts/build-search-index.ts` skriver det samme id ind i søge-indekset
 *      når det genereres fra kildekoden.
 *
 * Er de to uenige, peger et søgeresultat på et anker der ikke findes, og
 * brugeren lander bare øverst på siden uden at få at vide hvorfor. Derfor én
 * funktion, ét sted.
 *
 * Æ/Ø/Å translittereres frem for at fjernes, så "Værdi pr. opkald" og
 * "Verdi pr. opkald" ikke kollapser til samme slug.
 */

const DANISH: Record<string, string> = {
  æ: 'ae',
  ø: 'oe',
  å: 'aa',
  ä: 'ae',
  ö: 'oe',
  ü: 'ue',
  é: 'e',
  è: 'e',
  ê: 'e',
  á: 'a',
  à: 'a',
  í: 'i',
  ó: 'o',
  ú: 'u',
};

/**
 * 'Rater i drift (~F)' → 'rater-i-drift-f'.
 *
 * Tomt/whitespace-input giver tom streng — kalderen skal da UNDLADE at sætte et
 * id frem for at lave et anker der hedder ingenting.
 */
export function slugifyAnchor(text: string): string {
  const lowered = text
    .normalize('NFC')
    .toLowerCase()
    // Tekst i parentes er ofte en kilde-angivelse eller et symbol (fx "(~F)");
    // den bevares som ord, men tegnene selv falder væk nedenfor.
    .replace(/[æøåäöüéèêáàíóú]/g, (ch) => DANISH[ch] ?? ch);
  const slug = lowered
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    // Meget lange overskrifter giver uhåndterlige ankre; 60 tegn er rigeligt
    // til at være unikt på en side.
    .slice(0, 60)
    .replace(/-+$/g, '');
  return slug;
}

/** Anker-prefix, så vores id'er ikke kan kollidere med tilfældige id'er i DOM'en. */
export const ANCHOR_PREFIX = 'sec-';

/** Fuldt element-id for et afsnit. Tom titel → null (intet anker). */
export function anchorId(text: string): string | null {
  const slug = slugifyAnchor(text);
  return slug.length > 0 ? `${ANCHOR_PREFIX}${slug}` : null;
}

/**
 * Scroll blødt til et anker, så snart det FINDES ([D-198]).
 *
 * Hvorfor en ventetid og ikke bare `getElementById` med det samme: siderne
 * henter data asynkront og tegner først deres afsnit bagefter (`v-if="loading"`).
 * Et opslag umiddelbart efter navigationen ville derfor næsten altid fejle, og
 * brugeren ville lande øverst på siden uden at vide hvorfor. Vinduet er sat
 * rundhåndet, fordi et koldt dashboard i prod kan tage adskillige sekunder
 * ([D-196]-målingen) — findes ankeret aldrig, sker der ingenting, og brugeren
 * står på den rigtige side.
 *
 * Fremhævningen er kortvarig og bruger samme idé som ordbogens "Se også"-hop:
 * man skal kunne se HVOR man landede på en lang side.
 */
export function scrollToAnchorWhenReady(id: string, timeoutMs = 15_000): void {
  if (typeof document === 'undefined') return;
  const started = Date.now();
  const attempt = (): void => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      el.classList.add('anchor-flash');
      window.setTimeout(() => el.classList.remove('anchor-flash'), 1600);
      return;
    }
    if (Date.now() - started < timeoutMs) window.setTimeout(attempt, 120);
  };
  // Første forsøg efter næste frame: er siden allerede tegnet, rammer vi med det
  // samme og undgår en synlig forsinkelse.
  requestAnimationFrame(attempt);
}
